import { useCallback, useMemo } from "react";

import { CheckboxState } from "@adsk/alloy-react-checkbox";
import { cascadeMultiSelection, TreeSelectedIds } from "@adsk/alloy-react-tree";
import {
  DefaultInputTreeNode,
  InternalDataNode,
} from "@adsk/alloy-react-tree/es/types";

import { CategoryDTO } from "../../models";

export type TreeItem = {
  id: string;
  label: string;
  show: boolean;
  children: TreeItem[];
};

export type TreeUpdate = {
  id: string;
  isSelected: CheckboxState;
};

export const ALPHABETICAL_SORT = (a: TreeItem, b: TreeItem) =>
  a.label.localeCompare(b.label);

const useAlloyReactTree = () => {
  const buildChildren = useCallback(
    (
      data: CategoryDTO[],
      categoryId: string,
      categoryIdsUsed?: CategoryDTO["id"][]
    ): TreeItem[] => {
      const node = data.find((a) => a.id === categoryId);
      const subcategoryIds = node?.computedTreeFields?.subcategoryIds;

      if (!node || !subcategoryIds) {
        return [];
      }

      return subcategoryIds
        .map((subCategoryId) => {
          const subCategoryNode = data.find(
            (node) => node.id === subCategoryId
          );

          // This filters out inactive categories and their subcategories, unless we pass
          // CategoryIdsUsed, in which case we will include those categories that we need
          // to include regardless of whether they are active or not.
          if (!categoryIdsUsed && subCategoryNode?.isActive === false) {
            return;
          }

          return {
            id: subCategoryId,
            label: subCategoryNode?.name || "",
            show: true,
            children: buildChildren(data, subCategoryId, categoryIdsUsed),
          };
        })
        .filter((child): child is TreeItem => !!child)
        .sort(ALPHABETICAL_SORT);
    },
    []
  );

  const normalizeTreeData = useCallback(
    (
      data: CategoryDTO[],
      categoryIdsUsed?: CategoryDTO["id"][]
    ): TreeItem[] => {
      const root = data.find((node) => node.computedTreeFields?.isRoot);
      if (!root || !root.computedTreeFields) return [];

      return buildChildren(data, root.id, categoryIdsUsed);
    },
    [buildChildren]
  );

  const getTreeSelectedIds = useCallback(
    (
      changes: TreeUpdate,
      selectedIds: TreeSelectedIds,
      includeSubcategories: CheckboxState,
      normalizedTree: Record<string, InternalDataNode<DefaultInputTreeNode>>
    ): TreeSelectedIds => {
      if (includeSubcategories) {
        return cascadeMultiSelection({ changes, selectedIds, normalizedTree });
      } else {
        const treeNode = normalizedTree[changes.id];
        const parentIds = [...treeNode.path];
        const updatedSelectedIds: TreeSelectedIds = {
          ...selectedIds,
          [changes.id]: changes.isSelected,
        };
        // iterate up for each node, check if we need to update the checkbox state
        while (parentIds.length) {
          const currentParentNode =
            normalizedTree[parentIds[parentIds.length - 1]];
          const selectedChildCount = currentParentNode.childIds.reduce<number>(
            (count, childId) => {
              if (updatedSelectedIds[childId]) return count + 1;
              return count;
            },
            0
          );

          const currentParentCheckboxState =
            updatedSelectedIds[currentParentNode.id];
          let checkedState: CheckboxState = currentParentCheckboxState;
          // checkedState === true means user manually selected the category, we will not alter the state
          if (
            selectedChildCount === currentParentNode.childIds.length &&
            currentParentCheckboxState !== true
          ) {
            // all children selected
            checkedState = "indeterminate";
          } else if (selectedChildCount > 0 && checkedState !== true) {
            // some children selected
            checkedState = "indeterminate";
          } else if (
            selectedChildCount === 0 &&
            currentParentCheckboxState === "indeterminate"
          ) {
            // no children selected
            checkedState = false;
          }
          updatedSelectedIds[currentParentNode.id] = checkedState;
          parentIds.pop();
        }

        return updatedSelectedIds;
      }
    },
    []
  );

  return useMemo(() => {
    return {
      normalizeTreeData,
      getTreeSelectedIds,
    };
  }, [normalizeTreeData, getTreeSelectedIds]);
};

export default useAlloyReactTree;
